/**
 * "Preview sponsor portal" — the sponsor's own upload page, shown to staff.
 *
 * THE QUESTION IT ANSWERS is the one somebody has just before sending forty
 * invitations: does our branding land, does the deadline read the way we meant,
 * are we asking for the versions we think we are asking for.
 *
 * ITS OWN ROUTE, OPENED IN A NEW TAB. It used to take over the sponsors tab,
 * which meant the sponsor's page was rendered inside the admin layout, at the
 * width the admin layout left over, under a sidebar no sponsor will ever see.
 * That is a picture of the page rather than the page. On its own route there is
 * no shell around it and it lays out exactly as a sponsor's browser will lay it
 * out, which is the entire point of looking.
 *
 * A REAL URL ALSO MAKES IT SHAREABLE. The selected sponsor lives in the query
 * string, so the tab survives a refresh and the link can be pasted to a
 * colleague with "does this look right to you".
 *
 * IT RENDERS THE REAL COMPONENT. PortalView is the same code the sponsor's page
 * uses, given the same payload from the same server-side builder. A second
 * implementation would drift, and a preview that drifts is worse than no
 * preview, because its whole job is to be checked and believed.
 *
 * NOTHING IT SHOWS CAN BE ACTED ON. `readOnly` turns off every control, so
 * staff cannot upload, replace or confirm on a sponsor's behalf. Nothing is
 * minted either: the preview endpoint reads and returns, so unlike issuing a
 * link it cannot revoke the one the sponsor is holding. That distinction is why
 * this exists rather than a "sign in as them" button.
 *
 * PER SPONSOR, because that is what makes it truthful. An event's sponsors see
 * genuinely different pages: different tiers, different slots filled, carried
 * over artwork for the returning ones. A generic preview would show a page no
 * actual sponsor has.
 */

import { useCallback, useEffect, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';

import { PortalView } from './Portal.tsx';

/** Just enough of a sponsor to offer them in the picker. */
interface PreviewableSponsor {
  eventSponsorId: string;
  companyName: string;
}

export default function PortalPreview() {
  const { clientId = '', eventId = '' } = useParams();
  const [params, setParams] = useSearchParams();
  const selected = params.get('sponsor') ?? '';

  const [sponsors, setSponsors] = useState<PreviewableSponsor[] | null>(null);
  /* Typed as the component's own prop, so this file never restates the portal
     payload's shape and cannot fall behind it. */
  const [data, setData] = useState<Parameters<typeof PortalView>[0]['data'] | null>(null);
  const [error, setError] = useState<string | null>(null);

  /* Fetched here rather than handed in as a prop. The tab can be opened
     directly, refreshed, or arrive from a pasted link, none of which go through
     the sponsors table. */
  const loadSponsors = useCallback(async () => {
    try {
      const res = await fetch(`/api/clients/${clientId}/events/${eventId}/sponsors`);
      if (!res.ok) throw new Error(String(res.status));
      const body = (await res.json()) as { sponsors: PreviewableSponsor[] };
      setSponsors(body.sponsors);
      /* Default to the first, and put it in the URL so a refresh keeps it. */
      if (!selected && body.sponsors[0]) {
        setParams({ sponsor: body.sponsors[0].eventSponsorId }, { replace: true });
      }
    } catch {
      setError('Could not load the sponsors on this event.');
    }
  }, [clientId, eventId, selected, setParams]);

  useEffect(() => {
    void loadSponsors();
  }, [loadSponsors]);

  const loadPreview = useCallback(async () => {
    if (!selected) return;
    setData(null);
    setError(null);
    try {
      const res = await fetch(
        `/api/clients/${clientId}/events/${eventId}/sponsors/${selected}/portal-preview`,
      );
      if (!res.ok) throw new Error(String(res.status));
      const body = (await res.json()) as { portal: Parameters<typeof PortalView>[0]['data'] };
      setData(body.portal);
    } catch {
      setError('Could not load the preview for that sponsor.');
    }
  }, [clientId, eventId, selected]);

  useEffect(() => {
    void loadPreview();
  }, [loadPreview]);

  return (
    <div className="pv">
      {/* Fixed to the top, because the page below scrolls like a real portal
          and the one thing distinguishing the two must not scroll away. */}
      <div className="pv-bar">
        <div className="pv-bar-text">
          <b>Preview of the sponsor upload page</b>
          <span className="csub">
            Read only. Nothing here can be uploaded or changed, and no upload link is created, so
            the sponsor&rsquo;s own link keeps working.
          </span>
        </div>

        {sponsors && sponsors.length > 0 && (
          <label className="pv-pick">
            <span className="fpill-label">Previewing as</span>
            <select
              className="fpill-select"
              value={selected}
              aria-label="Preview as which sponsor"
              onChange={(e) => setParams({ sponsor: e.target.value }, { replace: true })}
            >
              {sponsors.map((s) => (
                <option key={s.eventSponsorId} value={s.eventSponsorId}>
                  {s.companyName}
                </option>
              ))}
            </select>
          </label>
        )}

        {/* A link back rather than a close button: this is its own tab now, and
            a "close" that cannot close a tab is a lie. Somebody who opened it
            from the sponsors table just closes the tab; somebody who arrived by
            a pasted link needs a way in. */}
        <Link
          className="btn-compact btn-compact--quiet"
          to={`/c/${clientId}/events/${eventId}`}
        >
          Back to Sponsors
        </Link>
      </div>

      {error ? (
        <p className="auth-error">{error}</p>
      ) : !sponsors ? (
        <div className="app-loading" aria-busy="true" />
      ) : sponsors.length === 0 ? (
        <div className="empty">
          <p className="empty-title">No sponsors to preview</p>
          <p className="empty-copy">
            Add a sponsor to this event and their upload page can be previewed here.
          </p>
        </div>
      ) : !data ? (
        <div className="app-loading" aria-busy="true" />
      ) : (
        /* No frame around it. On its own route there is no admin layout to
           separate it from, so the page renders exactly as the sponsor's
           browser will render it. */
        <PortalView data={data} readOnly onChanged={() => undefined} />
      )}
    </div>
  );
}
